// @ts-nocheck
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { AITaskType } from './useAI';

interface AIStats {
  totalInteractions: number;
  successfulInteractions: number;
  failedInteractions: number;
  interactionsByType: Record<AITaskType, number>;
  averageResponseLength: number;
}

export const useAIStats = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<AIStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Total de interações
        const { count: totalCount, error: totalError } = await supabase
          .from('ai_interactions')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id);
        if (totalError) throw totalError;

        // Interações bem-sucedidas
        const { count: successCount, error: successError } = await supabase
          .from('ai_interactions')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .eq('success', true);
        if (successError) throw successError;

        // Interações falhas
        const { count: failedCount, error: failedError } = await supabase
          .from('ai_interactions')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .eq('success', false);
        if (failedError) throw failedError;

        // Interações por tipo (agrupado) - supabase-js não possui group_by; fazemos no cliente
        const { data: interactionsRaw, error: interactionsByTypeError } = await supabase
          .from('ai_interactions')
          .select('task_type')
          .eq('user_id', user.id);
        if (interactionsByTypeError) throw interactionsByTypeError;

        const interactionsByType: Record<AITaskType, number> = {
          case_analysis: 0,
          document_generation: 0,
          deadline_management: 0,
          legal_research: 0,
          general: 0,
        };
        interactionsRaw?.forEach((item: any) => {
          if (item.task_type) {
            const key = item.task_type as AITaskType;
            interactionsByType[key] = (interactionsByType[key] || 0) + 1;
          }
        });

        // Média do tamanho da resposta (apenas para interações bem-sucedidas)
        const { data: avgResponseData, error: avgResponseError } = await supabase
          .from('ai_interactions')
          .select('response_length')
          .eq('user_id', user.id)
          .eq('success', true);
        if (avgResponseError) throw avgResponseError;

        const totalResponseLength = avgResponseData?.reduce((sum, item) => sum + (item.response_length || 0), 0) || 0;
        const averageResponseLength = successCount > 0 ? totalResponseLength / successCount : 0;

        setStats({
          totalInteractions: totalCount || 0,
          successfulInteractions: successCount || 0,
          failedInteractions: failedCount || 0,
          interactionsByType,
          averageResponseLength,
        });

      } catch (err: any) {
        console.error("Erro ao buscar estatísticas da IA:", err);
        setError(err.message || "Não foi possível carregar as estatísticas da IA.");
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [user]);

  return { stats, loading, error };
};
